/**
 * PATH: apps/quote/src/pages/QuotesPage.tsx
 * PURPOSE: Quote list view with search and filters
 */

import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Copy, Edit, Eye, Plus, RefreshCw, Search, Sparkles, Trash2 } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { cn, formatCurrency, formatDate, getStatusColor, getStatusLabel } from '@/lib/utils'
import { quoteApi, type QuoteListItem, type QuoteStatus } from '@/lib/quoteApi'

function toNumber(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const n = Number.parseFloat(value)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

export default function QuotesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<QuoteStatus | 'all'>('all')

  const quotesQuery = useQuery({
    queryKey: ['quotes', { status: statusFilter, search: searchQuery }],
    queryFn: async () =>
      quoteApi.listQuotes({
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: searchQuery || undefined,
      }),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => quoteApi.deleteQuote(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quotes'] }),
  })

  const duplicateMutation = useMutation({
    mutationFn: async (id: string) => quoteApi.duplicateQuote(id, false),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      if (created?.id) navigate(`/builder/${created.id}`)
    },
  })

  const quotes = quotesQuery.data || []

  const totalCountLabel = useMemo(() => {
    if (quotesQuery.isLoading) return 'Loading…'
    return `${quotes.length} quote${quotes.length === 1 ? '' : 's'}`
  }, [quotes.length, quotesQuery.isLoading])

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Quotes</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{totalCountLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/import"
            className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            <Sparkles className="h-4 w-4" />
            Import with AI
          </Link>
          <Link
            to="/builder"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            New Quote
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search quotes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as QuoteStatus | 'all')}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="pending_approval">Pending Approval</option>
          <option value="approved">Approved</option>
          <option value="sent">Sent</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
        </select>
        <button
          onClick={() => quotesQuery.refetch()}
          className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          disabled={quotesQuery.isFetching}
        >
          <RefreshCw className={cn('h-4 w-4', quotesQuery.isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Quotes Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        {quotesQuery.isError ? (
          <div className="p-6 text-sm text-red-700 dark:text-red-300">
            {(quotesQuery.error as Record<string, unknown>)?.message || 'Failed to load quotes'}
          </div>
        ) : null}
        {quotesQuery.isLoading ? (
          <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading quotes…</div>
        ) : null}
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Quote #</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Project</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Client</th>
              <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Amount</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Date</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Valid Until</th>
              <th className="px-6 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {quotes.map((quote: QuoteListItem) => (
              <tr key={quote.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4">
                  <Link to={`/quotes/${quote.id}`} className="font-medium text-blue-600 hover:text-blue-700">
                    {quote.quote_number}
                  </Link>
                </td>
                <td className="px-6 py-4 text-gray-900 dark:text-white">{quote.project_name || '—'}</td>
                <td className="px-6 py-4 text-gray-600 dark:text-gray-400">{quote.client_name || '—'}</td>
                <td className="px-6 py-4 text-right font-medium text-gray-900 dark:text-white">
                  {formatCurrency(toNumber(quote.total_incl_gst ?? quote.total_excl_gst))}
                </td>
                <td className="px-6 py-4">
                  <span className={cn('px-2 py-1 rounded-full text-xs font-medium', getStatusColor(quote.status))}>
                    {getStatusLabel(quote.status)}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                  {quote.quote_date || quote.created_at ? formatDate((quote.quote_date || quote.created_at) as string) : '—'}
                </td>
                <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                  {quote.valid_until ? formatDate(quote.valid_until) : '—'}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1">
                    <Link to={`/quotes/${quote.id}`} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg">
                      <Eye className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </Link>
                    <Link to={`/builder/${quote.id}`} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg">
                      <Edit className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </Link>
                    <button
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg"
                      title="Duplicate"
                      onClick={() => duplicateMutation.mutate(quote.id)}
                      disabled={duplicateMutation.isPending}
                    >
                      <Copy className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </button>
                    <button
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg"
                      title="Delete"
                      onClick={() => {
                        if (window.confirm('Delete this quote? (Draft only)')) deleteMutation.mutate(quote.id)
                      }}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!quotesQuery.isLoading && quotes.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  No quotes found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  )
}

